import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Trophy, RotateCcw, Check, X } from "lucide-react";

const questions = [
  {
    q: "Which planet is known as the Red Planet?",
    options: ["Venus", "Mars", "Jupiter", "Mercury"],
    answer: 1,
  },
  {
    q: "How many sides does a hexagon have?",
    options: ["Five", "Seven", "Six", "Eight"],
    answer: 2,
  },
  {
    q: "What is the largest ocean on Earth?",
    options: ["Atlantic", "Indian", "Arctic", "Pacific"],
    answer: 3,
  },
];

export default function MiniQuiz() {
  const [step, setStep] = useState(0);
  const [picked, setPicked] = useState(null);
  const [score, setScore] = useState(0);
  const done = step >= questions.length;

  const pick = (i) => {
    if (picked !== null) return;
    setPicked(i);
    if (i === questions[step].answer) setScore((s) => s + 1);
    setTimeout(() => {
      setPicked(null);
      setStep((s) => s + 1);
    }, 900);
  };

  const restart = () => {
    setStep(0);
    setPicked(null);
    setScore(0);
  };

  return (
    <section id="play" className="relative px-4 py-24 sm:px-8">
      <div className="absolute left-1/2 top-1/2 -z-10 h-[380px] w-[620px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#FF007A]/10 blur-[120px]" />
      <motion.div
        data-testid="mini-quiz-card"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="glass mx-auto max-w-xl rounded-3xl p-6 sm:p-9"
      >
        <p className="font-code text-[11px] tracking-[0.3em] text-[#00F0FF]">
          A TASTE OF THE ARENA
        </p>
        <AnimatePresence mode="wait">
          {done ? (
            <motion.div
              key="result"
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              className="py-8 text-center"
            >
              <Trophy size={44} className="mx-auto text-[#39FF14]" />
              <p className="font-display mt-5 text-3xl font-extrabold text-white" data-testid="mini-quiz-score">
                {score}/{questions.length}
              </p>
              <p className="mt-2 text-sm text-[#94A3B8]">
                {score === questions.length
                  ? "Flawless. The leaderboard is waiting."
                  : "Not bad. The app is where legends are made."}
              </p>
              <button
                onClick={restart}
                data-testid="mini-quiz-restart-button"
                className="btn-neon mx-auto mt-6 flex items-center gap-2 rounded-full border border-[#222738] px-6 py-2.5 font-sub text-sm font-semibold text-white hover:border-[#00F0FF]"
              >
                <RotateCcw size={14} /> Play again
              </button>
            </motion.div>
          ) : (
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -24 }}
              transition={{ duration: 0.35 }}
            >
              <div className="mt-5 flex items-center justify-between font-code text-[10px] text-[#64748B]">
                <span>
                  QUESTION {step + 1}/{questions.length}
                </span>
                <span className="text-[#39FF14]">SCORE {score}</span>
              </div>
              <h3 className="font-sub mt-3 text-lg font-semibold text-white sm:text-xl">
                {questions[step].q}
              </h3>
              <div className="mt-6 grid gap-3">
                {questions[step].options.map((o, i) => {
                  const isAnswer = i === questions[step].answer;
                  const isPicked = picked === i;
                  let cls = "border-[#222738] bg-[#0F111A] text-[#94A3B8] hover:border-[#00F0FF] hover:text-white";
                  if (picked !== null && isAnswer)
                    cls = "border-[#39FF14] bg-[#39FF14]/10 text-[#39FF14] shadow-[0_0_20px_rgba(57,255,20,0.25)]";
                  else if (picked !== null && isPicked)
                    cls = "border-[#FF007A] bg-[#FF007A]/10 text-[#FF007A]";
                  return (
                    <button
                      key={o}
                      data-testid={`mini-quiz-option-${i}`}
                      onClick={() => pick(i)}
                      className={`flex items-center justify-between rounded-xl border px-4 py-3 text-left font-sub text-sm transition-all duration-200 ${cls}`}
                    >
                      {o}
                      {picked !== null && isAnswer && <Check size={16} />}
                      {picked !== null && isPicked && !isAnswer && <X size={16} />}
                    </button>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </section>
  );
}
